import { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAuth } from "../contexts/AuthContext";

const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Août","Sep","Oct","Nov","Déc"];

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", fontSize: 13, width: "100%" },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", flex: 1 },
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #f3f4f6", fontSize: 13 },
};

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function StatusBadge({ status }) {
  const map = { draft: { l: "Brouillon", c: "#4b5563" }, submitted: { l: "Soumise", c: "#185fa5" }, paid: { l: "Payée", c: "#1a9e72" } };
  const { l, c } = map[status] || { l: status, c: "#4b5563" };
  return <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, fontWeight: 700, background: c + "22", color: c }}>{l}</span>;
}

export default function TVA({ currentCompanyId, currentSociete }) {
  const { getToken } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [ca3, setCa3] = useState(null);
  const [history, setHistory] = useState([]);
  const [histYear, setHistYear] = useState(now.getFullYear());
  const [loading, setLoading] = useState(false);
  const [declaring, setDeclaring] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const call = useCallback(async (path, method = "GET", body = null) => {
    const res = await fetch("/api/tva" + path, {
      method,
      headers: { Authorization: "Bearer " + getToken(), "Content-Type": "application/json" },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [getToken]);

  const loadSummary = useCallback(async () => {
    if (!currentCompanyId) return;
    setLoading(true);
    try {
      const d = await call(`/summary?company_id=${currentCompanyId}&year=${year}&month=${month}`);
      setSummary(d);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [currentCompanyId, year, month, call]);

  const loadCa3 = useCallback(async () => {
    if (!currentCompanyId) return;
    try {
      const d = await call(`/ca3?company_id=${currentCompanyId}&year=${year}&month=${month}`);
      setCa3(d);
    } catch (e) { console.error(e); }
  }, [currentCompanyId, year, month, call]);

  const loadHistory = useCallback(async () => {
    if (!currentCompanyId) return;
    try {
      const d = await call(`/history?company_id=${currentCompanyId}`);
      setHistory(Array.isArray(d) ? d : []);
    } catch (e) { setHistory([]); }
  }, [currentCompanyId, call]);

  useEffect(() => { if (tab === "dashboard") loadSummary(); }, [tab, loadSummary]);
  useEffect(() => { if (tab === "ca3") { loadCa3(); } }, [tab, loadCa3]);
  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);

  const declare = async () => {
    if (!ca3 && !summary) return showToast("Chargez d'abord les données", "error");
    setDeclaring(true);
    try {
      const pad = String(month).padStart(2, "0");
      const lastDay = new Date(year, month, 0).getDate();
      await call("/declare", "POST", {
        company_id: currentCompanyId,
        period_start: `${year}-${pad}-01`,
        period_end: `${year}-${pad}-${lastDay}`,
        declaration_type: "CA3",
        tva_collectee: summary?.tva_collectee || 0,
        tva_deductible: summary?.tva_deductible || 0,
        tva_nette: summary?.tva_nette || 0,
      });
      showToast("Déclaration CA3 enregistrée");
      loadHistory();
    } catch (e) { showToast("Erreur déclaration", "error"); } finally { setDeclaring(false); }
  };

  const TABS = [
    { id: "dashboard", label: "📊 Tableau de bord" },
    { id: "ca3",       label: "📋 Déclaration CA3" },
    { id: "intracom",  label: "🌍 Intracommunautaire" },
    { id: "history",   label: "🗂️ Historique" },
  ];

  const monthlyChart = summary?.monthly_data || MONTHS.map((m, i) => ({ month: m, collectee: 0, deductible: 0, nette: 0 }));
  const netColor = (summary?.tva_nette || 0) >= 0 ? "#ef4444" : "#1a9e72";

  const histFiltered = history.filter(h => {
    if (!histYear) return true;
    return h.period_start?.startsWith(String(histYear));
  });

  return (
    <div style={{ padding: "0 0 40px" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 20px", borderRadius: 10, background: toast.type === "error" ? "#ef4444" : "#1a9e72", color: "#fff", fontWeight: 700, fontSize: 13 }}>{toast.msg}</div>
      )}

      {/* Bandeau d'avertissement (06/10/2026) : la CA3 ignore aujourd'hui la
          TVA déductible des factures d'achat et les factures « envoyées ».
          À retirer quand la CA3 calculée sur les écritures sera en place. */}
      <div role="alert" style={{ marginBottom: 18, padding: "12px 16px", borderRadius: 10, background: "#fef3c7", border: "1px solid #f59e0b", color: "#78350f", fontSize: 13.5, fontWeight: 600, lineHeight: 1.45 }}>
        ⚠️ Déclaration de TVA en cours de refonte : ne pas utiliser pour déclarer, rapprochez-vous de votre expert-comptable.
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>🌍 TVA & Déclarations fiscales</h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{currentSociete}</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ ...S.input, width: 120 }}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...S.input, width: 90 }}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 16px 10px" }}>{t.label}</button>
        ))}
      </div>

      {/* ── Dashboard ── */}
      {tab === "dashboard" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            <div style={{ ...S.kpi, minWidth: 160 }}>
              <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>TVA Collectée</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#185fa5" }}>{fmt(summary?.tva_collectee)} €</div>
              <div style={{ fontSize: 11, color: "#4b5563", marginTop: 4 }}>Sur factures envoyées / payées</div>
            </div>
            <div style={{ ...S.kpi, minWidth: 160 }}>
              <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>TVA Déductible</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#8b5cf6" }}>{fmt(summary?.tva_deductible)} €</div>
              <div style={{ fontSize: 11, color: "#4b5563", marginTop: 4 }}>Sur notes de frais et achats</div>
            </div>
            <div style={{ ...S.kpi, minWidth: 160 }}>
              <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>Net à {(summary?.tva_nette || 0) >= 0 ? "payer" : "récupérer"}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: netColor }}>{fmt(Math.abs(summary?.tva_nette || 0))} €</div>
              <div style={{ fontSize: 11, color: "#4b5563", marginTop: 4 }}>{MONTHS[month - 1]} {year}</div>
            </div>
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Évolution TVA — {year}</div>
            {loading ? <div style={{ color: "#4b5563", textAlign: "center", padding: 40 }}>Chargement…</div> : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={monthlyChart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#185fa5" stopOpacity={0.3} /><stop offset="95%" stopColor="#185fa5" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gd" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} /><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} /></linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fill: "#4b5563", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8 }} labelStyle={{ color: "#111827" }} formatter={v => fmt(v) + " €"} />
                  <Legend />
                  <Area type="monotone" dataKey="collectee" name="Collectée" stroke="#185fa5" fill="url(#gc)" />
                  <Area type="monotone" dataKey="deductible" name="Déductible" stroke="#8b5cf6" fill="url(#gd)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}

      {/* ── CA3 ── */}
      {tab === "ca3" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 20 }}>
          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#111827", marginBottom: 20 }}>Déclaration CA3 — {MONTHS[month - 1]} {year}</div>

            <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb" }}>
              <div style={{ background: "#f9fafb", padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.05em" }}>A — Montant des opérations réalisées</div>
              {[
                { label: "CA total imposable HT", value: ca3?.ca_total, highlight: false },
                { label: "dont Opérations taxables à 20%", value: ca3?.ops_20, highlight: false },
                { label: "dont Opérations taxables à 10%", value: ca3?.ops_10, highlight: false },
                { label: "dont Opérations taxables à 5,5%", value: ca3?.ops_55, highlight: false },
                { label: "dont Opérations exonérées", value: ca3?.ops_exo, highlight: false },
              ].map(r => (
                <div key={r.label} style={{ ...S.row, background: r.highlight ? "rgba(26,158,114,0.06)" : "transparent" }}>
                  <span style={{ color: "#4b5563" }}>{r.label}</span>
                  <span style={{ fontWeight: 700, color: "#111827" }}>{fmt(r.value)} €</span>
                </div>
              ))}
              <div style={{ background: "#f9fafb", padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 4 }}>B — TVA due</div>
              {[
                { label: "TVA brute collectée", value: ca3?.tva_brute },
                { label: "Total TVA due (ligne 16)", value: ca3?.tva_brute, bold: true },
              ].map(r => (
                <div key={r.label} style={{ ...S.row }}>
                  <span style={{ color: r.bold ? "#111827" : "#4b5563", fontWeight: r.bold ? 700 : 400 }}>{r.label}</span>
                  <span style={{ fontWeight: 700, color: "#185fa5" }}>{fmt(r.value)} €</span>
                </div>
              ))}
              <div style={{ background: "#f9fafb", padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 4 }}>C — TVA déductible</div>
              {[
                { label: "TVA déductible sur immobilisations", value: ca3?.tva_ded_immos },
                { label: "TVA déductible sur autres biens/services", value: ca3?.tva_ded_services },
                { label: "Total TVA déductible (ligne 23)", value: (ca3?.tva_ded_immos || 0) + (ca3?.tva_ded_services || 0), bold: true },
              ].map(r => (
                <div key={r.label} style={{ ...S.row }}>
                  <span style={{ color: r.bold ? "#111827" : "#4b5563", fontWeight: r.bold ? 700 : 400 }}>{r.label}</span>
                  <span style={{ fontWeight: 700, color: "#8b5cf6" }}>{fmt(r.value)} €</span>
                </div>
              ))}
              <div style={{ ...S.row, background: "rgba(26,158,114,0.08)", borderTop: "2px solid rgba(26,158,114,0.3)" }}>
                <span style={{ fontWeight: 800, color: "#111827", fontSize: 14 }}>TVA nette à payer (ligne 28)</span>
                <span style={{ fontWeight: 800, fontSize: 16, color: (summary?.tva_nette || 0) >= 0 ? "#ef4444" : "#1a9e72" }}>{fmt(summary?.tva_nette || 0)} €</span>
              </div>
            </div>
          </div>

          <div>
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Actions</div>
              <button onClick={declare} disabled={declaring} style={{ ...S.btn, background: "#1a9e72", color: "#fff", width: "100%", marginBottom: 10, opacity: declaring ? 0.6 : 1 }}>
                {declaring ? "Enregistrement…" : "📨 Valider et déclarer CA3"}
              </button>
              <button onClick={loadCa3} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563", width: "100%" }}>🔄 Recalculer</button>
            </div>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Récapitulatif</div>
              <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 6 }}>Période : <span style={{ color: "#111827" }}>{MONTHS[month - 1]} {year}</span></div>
              <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 6 }}>Régime : <span style={{ color: "#111827" }}>Mensuel (CA3)</span></div>
              <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 6 }}>Échéance : <span style={{ color: "#f59e0b" }}>24/{String(month + 1 > 12 ? 1 : month + 1).padStart(2, "0")}/{month + 1 > 12 ? year + 1 : year}</span></div>
              <div style={{ marginTop: 16, padding: 12, background: "rgba(239,68,68,0.08)", borderRadius: 8, border: "1px solid rgba(239,68,68,0.2)", fontSize: 12, color: "#ef4444" }}>
                ⚠️ Vérifiez votre déclaration avant soumission à l'administration fiscale.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Intracom ── */}
      {tab === "intracom" && (
        <div>
          <div style={{ background: "rgba(24,95,165,0.1)", border: "1px solid rgba(24,95,165,0.3)", borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13, color: "#60a5fa" }}>
            ℹ️ TVA intracommunautaire : les achats auprès de fournisseurs UE (acquisitions) et les ventes à des clients UE sans TVA (livraisons) sont soumis à déclaration.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Acquisitions intracommunautaires</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  {["Fournisseur", "Pays", "N° TVA", "Montant HT"].map(h => <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: "#4b5563" }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: "#4b5563", fontSize: 13 }}>Aucune acquisition intracommunautaire ce mois</td></tr>
                </tbody>
              </table>
            </div>
            <div style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Livraisons intracommunautaires</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  {["Client", "Pays", "N° TVA", "Montant HT"].map(h => <th key={h} style={{ padding: "6px 8px", textAlign: "left", color: "#4b5563" }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  <tr><td colSpan={4} style={{ padding: 20, textAlign: "center", color: "#4b5563", fontSize: 13 }}>Aucune livraison intracommunautaire ce mois</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ ...S.card, marginTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Déclaration DES / DEB</div>
            <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 12 }}>
              La Déclaration Européenne de Services (DES) est mensuelle pour les prestations de services à des opérateurs UE. La Déclaration d'Échanges de Biens (DEB) a été remplacée par Intrastat depuis 2022.
            </div>
            <button style={{ ...S.btn, background: "#185fa5", color: "#fff" }}>📤 Générer fichier DES (DEBWEB)</button>
          </div>
        </div>
      )}

      {/* ── History ── */}
      {tab === "history" && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Historique des déclarations</div>
            <select value={histYear} onChange={e => setHistYear(Number(e.target.value))} style={{ ...S.input, width: 100 }}>
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                {["Période", "Type", "TVA collectée", "TVA déductible", "Net", "Statut", "Date soumission"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {histFiltered.map(d => (
                <tr key={d.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "10px 12px", color: "#111827", fontWeight: 600 }}>{d.period_start?.slice(0, 7)}</td>
                  <td style={{ padding: "10px 12px", color: "#4b5563" }}>{d.declaration_type}</td>
                  <td style={{ padding: "10px 12px", color: "#185fa5", fontWeight: 700 }}>{fmt(d.tva_collectee)} €</td>
                  <td style={{ padding: "10px 12px", color: "#8b5cf6", fontWeight: 700 }}>{fmt(d.tva_deductible)} €</td>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: (d.tva_nette || 0) >= 0 ? "#ef4444" : "#1a9e72" }}>{fmt(d.tva_nette)} €</td>
                  <td style={{ padding: "10px 12px" }}><StatusBadge status={d.status} /></td>
                  <td style={{ padding: "10px 12px", color: "#4b5563" }}>{d.submitted_at ? d.submitted_at.slice(0, 10) : "—"}</td>
                </tr>
              ))}
              {histFiltered.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: "center", color: "#4b5563" }}>Aucune déclaration pour {histYear}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
